import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { AppBar, Avatar, Backdrop, Box, Button, Card, CardContent, CardHeader, CircularProgress, Container, makeStyles, TextField, Toolbar, Typography } from '@material-ui/core';
import { Alert, Rating } from '@material-ui/lab';
import clsx from 'clsx';
import { isEmpty } from 'lodash';
import { intendedServiceIdState, STEP_STATE_LIVE } from 'pages/CustomerBooking/RecoilState';
import { stepState } from 'pages/CustomerBooking/RecoilState';
import { bookingState, clientState } from 'pages/CustomerBooking/RecoilState';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Redirect, useHistory } from 'react-router-dom';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { getRatingMatrix, getTipStatuses, sendRatingForAppointment } from 'services/ratings.service';
import { personNameFull } from 'util/format';
import { useTrackEvent, useTrackPage } from 'util/trackEvent';
import { Provider, PROVIDER_CACHE } from '../BookingDetail/Provider';
import { ServiceFinishedPopup } from '../ServiceComplete';
import { RatingMatrix } from './RatingMatrix';
import { TipMatrix } from './TipMatrix';

const useStyles = makeStyles((theme) => {
  return {
    center: {
      textAlign: 'center',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
      "& > *": {
        marginBottom: theme.spacing(2),
      },
    },
    avatar: {
      height: theme.spacing(10),
      width: theme.spacing(10),
      marginBottom: theme.spacing(2),
    },
    emoji: {
      width: 'auto',
      height: theme.spacing(10)
    },
    header: {
      textAlign: 'center',
    },
    backdrop: {
      zIndex: theme.zIndex.drawer + 1,
      color: '#fff',
    },
    profile: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
    },
    providerName: {
      fontWeight: 'bold',
    }
  }
})

const RATING_LABELS = [
  "",
  "Awful",
  "Poor",
  "Fair",
  "Good",
  "Awesome"
]

const PostRating = () => {
  const classes = useStyles()
  const [rating, setRating] = useState(3);
  const booking = useRecoilValue(bookingState)
  const clientData = useRecoilValue(clientState)
  const [loading, setLoading] = useState(false);
  const history = useHistory()
  const setStepState = useSetRecoilState(stepState)

  useEffect(() => {
    setStepState(STEP_STATE_LIVE)
  }, []);

  const [subRatings, setSubRatings] = useState([]);
  const [comment, setComment] = useState('');
  const [amount, setAmount] = useState(0);
  const [tipValue, setTipValue] = useState();
  const [tipEnabled, setTipEnabled] = useState(false);
  const [tipMatrix, setTipMatrix] = useState([]);

  const intendedServiceId = useRecoilValue(intendedServiceIdState)
  const setIntendedServiceId = useSetRecoilState(intendedServiceIdState)

  const trackEvent = useTrackEvent()
  const trackPage = useTrackPage()

  const fetchTipStatus = useCallback(
    async () => {
      const result = await getTipStatuses(clientData?.appointment?.id)
      setTipMatrix(result?.tips)
      setTipEnabled(!result?.tip_disabled)
    },
    [clientData?.appointment?.id],
  );

  useEffect(() => {
    if (clientData?.appointment?.id) {
      fetchTipStatus()
    }
  }, [clientData?.appointment?.id]);

  useEffect(
    () => {
      if (intendedServiceId) {
        trackPage('Rating', { intendedBooking: intendedServiceId })
        trackEvent('Service Finished Dialog', { intendedBooking: intendedServiceId })
      } else {
        trackPage('Rating', {})
        trackEvent('Service Finished Dialog', {})
      }
    }, []
  );

  const providerName = useMemo(() => {
    const name = personNameFull(booking?.provider)
    const p = PROVIDER_CACHE[booking?.provider?.provider_type]
    const title = [p?.prefix, name, p?.suffix].filter(t => !isEmpty(t)).join(' ')
    return title

  }, [booking?.provider]);

  const onSubmit = useCallback(
    async () => {
      setLoading(true);
      try {
        const buildParams = {
          rating,
          comment,
          amount: tipValue ? tipValue : 0,
          tags: subRatings.map(tag => { return { id: tag?.id } }),
        }
        await sendRatingForAppointment(clientData?.appointment?.id, buildParams);

      } catch (e) {

      } finally {
        if (intendedServiceId) {
          setIntendedServiceId(null)
          trackEvent('Rating - Submit - Return to Booking', { intendedBooking: intendedServiceId, link: '/bookings/' + intendedServiceId + history.location.search })
          history.replace('/bookings/' + intendedServiceId + history.location.search)
        } else {
          trackEvent('Rating - Submit - Return to Booking', {link: '/bookings/' + booking?.id + history.location.search })
          history.replace('/bookings/' + booking?.id + history.location.search)
        }
        setLoading(false)
      }

    },
    [booking?.id, clientData?.appointment?.id, comment, history, rating, subRatings, tipValue, intendedServiceId],
  );

  // if (clientData?.state !== 'rating') {
  //   return <Redirect to={`/bookings/${booking?.id}/`} />
  // }


  return (<>
    <ServiceFinishedPopup />
    <AppBar elevation={0} position="static" color="secondary" className={clsx(classes.top, classes.appBar)}>
      <Toolbar>
        <Typography variant="h6" className={classes.title}>
          Feedback
        </Typography>
      </Toolbar>
    </AppBar>
    <Card className={classes.root}>
      <CardContent>
        <Container maxWidth="xs">
          <Box className={classes.profile}>
            <Avatar src={booking?.provider?.profile_pic_url} className={classes.avatar} />
            <Typography variant="body2" color="textPrimary" className={classes.providerName}>{providerName}</Typography>
            <Typography variant="body2" color="textSecondary">{PROVIDER_CACHE[booking?.provider?.provider_type]?.label}</Typography>
          </Box>

        </Container>
      </CardContent>
      <CardContent>
        <Alert severity="info">Please rate your previous provider before you can continue</Alert>
      </CardContent>
      <CardContent className={classes.center}>
        <Typography variant="h6">Please rate your provider</Typography>
        <img className={classes.emoji} alt='' src={`/assets/images/rating/icon_rating_emoji_${rating}.svg`} />
        <Typography variant="h4">{RATING_LABELS[rating]}</Typography>
        <Rating name="rating" value={rating} onChange={(evt, value) => {
          setRating(value)
          trackEvent('Rating - changed rating stars', { rating: value })
        }} />
      </CardContent>
      <CardContent className={classes.center}>
        {!isEmpty(clientData?.appointment?.provider?.provider_type) &&
          <RatingMatrix onSelect={(values) => setSubRatings(values)} rating={rating} subType={clientData?.appointment?.provider?.provider_type} />
        }
      </CardContent>
      <CardContent className={classes.center}>
        {tipEnabled && (<>
          <Typography variant="body2"><strong>Great service? Consider giving a tip.</strong></Typography>
          <TipMatrix onSelect={(value) => setTipValue(value)} tipValue={tipValue} tipMatrix={tipMatrix} />
        </>
        )}
      </CardContent>

      <CardContent >
        <Container maxWidth="xs" className={classes.center}>
          <TextField value={comment} onChange={e => setComment(e.target.value)} variant="filled" className={classes.comment} placeholder='Loved it?  Please let us know about your experience and service provider' multiline rows={4} fullWidth />
          <Button onClick={() => onSubmit()} fullWidth variant='contained' color="primary">Submit</Button>
        </Container>
      </CardContent>
    </Card>
    <Backdrop className={classes.backdrop} open={loading}>
      <CircularProgress color="inherit" />
    </Backdrop>
  </>
  )
}

export { PostRating };
